import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!token) { setError("Lien invalide ou expiré."); setLoading(false); return; }
    api.get(`/auth/reset-password/${token}`)
      .then(() => { if (!ignore) setValid(true); })
      .catch(() => { if (!ignore) setError("Ce lien est invalide ou a expiré. Demandez-en un nouveau."); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setSubmitting(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-left">
      <div className="hidden md:flex flex-col justify-between bg-[#0a0a0a] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "42px 42px" }} />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #FF6B35, transparent 70%)" }} />
        <div className="relative"><img src="/innova-fact-white.png" alt="innova-fact" className="h-14 w-auto" /></div>
        <div className="relative">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tighter leading-none">
            Nouveau<br /><span className="text-primary">mot de passe.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-sm leading-relaxed">Choisissez un mot de passe sécurisé pour votre accès au portail.</p>
        </div>
        <div className="relative text-xs text-white/50 tracking-wider uppercase">Portail sécurisé</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2 mb-8"><img src="/innova-fact-dark.png" alt="innova-fact" className="h-9 w-auto" /></div>

          {loading ? (
            <div className="text-sm text-muted-foreground tracking-widest uppercase animate-pulse">Vérification du lien…</div>
          ) : done ? (
            <div data-testid="reset-done" className="space-y-4">
              <div className="h-11 w-11 rounded-sm bg-emerald-50 border border-emerald-200 flex items-center justify-center"><CheckCircle size={22} className="text-emerald-700" /></div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Mot de passe modifié</h2>
              <p className="text-sm text-muted-foreground">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <Button data-testid="reset-goto-login" className="rounded-sm" onClick={() => navigate("/login")}>Aller à la connexion <ArrowRight size={16} className="ml-2" /></Button>
            </div>
          ) : !valid ? (
            <div data-testid="reset-error" className="space-y-4">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Lien invalide</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" className="rounded-sm" onClick={() => navigate("/mot-de-passe-oublie")}>Demander un nouveau lien</Button>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Choisir un nouveau mot de passe</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Au moins 8 caractères.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pw" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Nouveau mot de passe</Label>
                  <Input id="pw" data-testid="reset-password" type="password" required className="rounded-sm h-11" placeholder="min. 8 caractères" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cpw" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirmer</Label>
                  <Input id="cpw" data-testid="reset-confirm" type="password" required className="rounded-sm h-11" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                {error && <div data-testid="reset-form-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2">{error}</div>}
                <Button type="submit" data-testid="reset-submit" disabled={submitting} className="w-full h-11 rounded-sm group">
                  {submitting ? "Modification…" : "Modifier le mot de passe"}
                  <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
